import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { LIVE_REPORT_CAPTURE_CATEGORIES, LiveReportCaptureCategory } from '@shared/models';
import { ToggleRow } from '../toggle-row';

/** One event type as the picker shows it: what it is called, and what it covers. */
interface EventTypeRow {
  type: LiveReportCaptureCategory;
  label: string;
  description: string;
}

/**
 * The rows, in the order they are read.
 *
 * This is a reading order, not the payload's: the procedure steps come first because that is the
 * spine of a report, where {@link LIVE_REPORT_CAPTURE_CATEGORIES} keeps the order the API documents and
 * is what the emitted list is built from. The two are deliberately apart — a change of design
 * must not change the shape of what is stored.
 *
 * The wording is the picker's own: `milestone` reads as "Procedure steps" here, because that is
 * what it is to the person configuring a room.
 */
const EVENT_TYPE_ROWS: readonly EventTypeRow[] = [
  {
    type: 'milestone',
    label: 'Procedure steps',
    description: 'Milestones captured in the room',
  },
  { type: 'device', label: 'Devices', description: 'Wires, sheaths, stents, balloons' },
  { type: 'medication', label: 'Medications', description: 'Drugs, doses and route' },
  { type: 'imaging', label: 'Imaging', description: 'Angiography, OCT, control runs' },
  { type: 'anatomy', label: 'Anatomy', description: 'Target lesion and flow' },
  { type: 'hemodynamic', label: 'Hemodynamics', description: 'Pressures, gradients, FFR' },
  { type: 'decision', label: 'Decisions', description: 'Strategy and rationale' },
  { type: 'incident', label: 'Incidents', description: 'Events flagged for review' },
];

/** Distinguishes the group heading of one instance from another's. */
let nextId = 0;

/**
 * Picks which event types a report shows, one switch per type.
 *
 * **It speaks the stored shape, not the shown one.** In and out, the list is the types that are
 * *hidden* — `disabledEventTypes`, the field the API persists — while the switches read the other
 * way round, on meaning displayed. Both halves of that inversion live here, so no host has to
 * perform it and none can get it backwards.
 *
 * Storing the hidden types rather than the shown ones is what makes an event type added later
 * appear by default on rooms already configured, instead of being hidden by a list written before
 * it existed. The emitted list keeps the order the API documents and carries no duplicate,
 * whatever came in.
 *
 * ```html
 * <mai-event-types-picker
 *   [hiddenTypes]="settings().disabledEventTypes"
 *   (hiddenTypesChange)="save({ disabledEventTypes: $event })"></mai-event-types-picker>
 * ```
 */
@Component({
  selector: 'mai-event-types-picker',
  imports: [ToggleRow],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './event-types-picker.html',
})
export class EventTypesPicker {
  /** Accessible name of the group, for a screen that needs a wording of its own. */
  readonly legend = input('Event types');
  /** The types hidden from the report. Empty means every type shows. */
  readonly hiddenTypes = input<readonly LiveReportCaptureCategory[]>([]);
  /** Locks the switches while the host's write is in flight. */
  readonly pending = input(false);

  /** Emits the new list of hidden types, in the documented order and without duplicates. */
  readonly hiddenTypesChange = output<LiveReportCaptureCategory[]>();

  protected readonly rows = EVENT_TYPE_ROWS;
  protected readonly headingId = `mai-event-types-picker-${nextId++}`;

  /** The hidden types as a set, for a per-row lookup that does not walk the list eight times. */
  private readonly hidden = computed(() => new Set(this.hiddenTypes()));

  /** A switch is on when its type is *not* hidden. This is the inversion, in one place. */
  protected isDisplayed(type: LiveReportCaptureCategory): boolean {
    return !this.hidden().has(type);
  }

  /**
   * Shows or hides one type.
   *
   * The whole list is rebuilt rather than amended, because the field it feeds replaces what is
   * stored instead of adding to it. Rebuilding it from the documented order through a set is what
   * keeps it ordered and free of duplicates.
   *
   * @param type - The event type whose switch was flipped
   * @param displayed - Whether the type should now show
   */
  protected toggle(type: LiveReportCaptureCategory, displayed: boolean): void {
    const hidden = new Set(this.hidden());
    if (displayed) {
      hidden.delete(type);
    } else {
      hidden.add(type);
    }
    this.hiddenTypesChange.emit(
      LIVE_REPORT_CAPTURE_CATEGORIES.filter((candidate) => hidden.has(candidate)),
    );
  }
}
